import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Remarkable } from 'remarkable';
import type { ChatMessage, LocalizationLike } from './useAssistantChat';
import {
    AssistantMessage,
    EmptyState,
    JumpToLatest,
    Markdown,
    MessageMeta,
    MessagesScroller,
    MessagesViewport,
    TypingIndicator,
    UserMessage
} from './styled';
import { ArrowDownIcon } from './icons';

const renderer = new Remarkable({
    breaks: true,
    html: false
});

// Distance from the bottom (px) within which we keep following new messages.
const STICK_THRESHOLD = 32;

function renderMarkdown(text: string) {
    return { __html: renderer.render(text) };
}

function formatMeta(message: ChatMessage, localization: LocalizationLike) {
    const parts: string[] = [];
    if (message.sources && message.sources.length > 0) {
        parts.push(localization.getText('assistant_chat.sources', { sources: message.sources.join(', ') }));
    }
    if (typeof message.timingMs === 'number') {
        parts.push(localization.getText('assistant_chat.response_time', { seconds: (message.timingMs / 1000).toFixed(1) }));
    }
    return parts.join(' · ');
}

interface MessageListProps {
    localization: LocalizationLike;
    history: ChatMessage[];
    isSending: boolean;
    isInitializing: boolean;
}

export function MessageList({ localization, history, isSending, isInitializing }: MessageListProps) {
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const isPinnedRef = useRef(true);
    const [showJump, setShowJump] = useState(false);

    const scrollToBottom = (behavior: ScrollBehavior = 'auto') => {
        const el = scrollerRef.current;
        if (!el) return;
        if (typeof el.scrollTo === 'function') {
            el.scrollTo({ top: el.scrollHeight, behavior });
        } else {
            el.scrollTop = el.scrollHeight;
        }
    };

    // Opening the panel always starts at the latest message.
    useLayoutEffect(() => {
        scrollToBottom();
    }, []);

    // Follow new content only if the user is already at the bottom, or if they just sent a message.
    const lastMessage = history[history.length - 1];
    useLayoutEffect(() => {
        if (isPinnedRef.current || lastMessage?.role === 'user') {
            scrollToBottom();
            isPinnedRef.current = true;
            setShowJump(false);
        }
    }, [history.length, isSending]);

    useEffect(() => {
        const el = scrollerRef.current;
        if (!el) return;
        const onScroll = () => {
            const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
            const pinned = distance <= STICK_THRESHOLD;
            isPinnedRef.current = pinned;
            setShowJump(!pinned);
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => el.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <MessagesViewport>
            <MessagesScroller ref={scrollerRef} data-testid="assistant-chat-messages" role="log" aria-live="polite">
                {history.length === 0 && (
                    <EmptyState>
                        {isInitializing
                            ? localization.getText('assistant_chat.initializing')
                            : localization.getText('assistant_chat.welcome')}
                    </EmptyState>
                )}

                {history.map((message, index) =>
                    message.role === 'user' ? (
                        <UserMessage key={`user-${index}`}>{message.content}</UserMessage>
                    ) : (
                        <AssistantMessage key={`assistant-${index}`}>
                            <Markdown dangerouslySetInnerHTML={renderMarkdown(message.content)} />
                            {formatMeta(message, localization) && <MessageMeta>{formatMeta(message, localization)}</MessageMeta>}
                        </AssistantMessage>
                    )
                )}

                {isSending && (
                    <AssistantMessage>
                        <TypingIndicator
                            role="status"
                            aria-label={localization.getText('assistant_chat.thinking')}
                            title={localization.getText('assistant_chat.thinking')}>
                            <span />
                            <span />
                            <span />
                        </TypingIndicator>
                    </AssistantMessage>
                )}
            </MessagesScroller>

            {showJump && (
                <JumpToLatest
                    type="button"
                    onClick={() => scrollToBottom('smooth')}
                    title={localization.getText('assistant_chat.scroll_to_bottom')}
                    aria-label={localization.getText('assistant_chat.scroll_to_bottom')}>
                    <ArrowDownIcon size={16} />
                </JumpToLatest>
            )}
        </MessagesViewport>
    );
}
